import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import QuoteForm from "@/components/QuoteForm";
import {
  Trash2, HardHat, Truck,
  Phone, Mail, ArrowRight, Check, Zap, MapPin, DollarSign, Clock
} from "lucide-react";

const SERVICES = [
  {
    icon: Trash2,
    title: "Residential Cleanouts",
    desc: "Homes, apartments, garages, basements, and rentals. Furniture, appliances, boxes, general junk — we haul it all.",
  },
  {
    icon: HardHat,
    title: "Construction Debris Removal",
    desc: "Drywall, wood, flooring, tile, cabinets, doors, trim, and full jobsite cleanup. We keep contractors moving.",
  },
  {
    icon: Truck,
    title: "Dump Runs & Truckload Hauling",
    desc: "You loaded it — we'll take it. Full truckload hauling for big jobs, one flat price, straight to the dump.",
  },
];

const STEPS = [
  { n: "01", title: "Give Us A Call", desc: "Ring (973) 306-9206 or send a quick message. Tell us what needs to go." },
  { n: "02", title: "Get a Fair Quote", desc: "We talk through the job on the phone and give you an honest price on the spot." },
  { n: "03", title: "Book The Haul", desc: "Pick a day that works. We handle the loading, hauling, and disposal." },
  { n: "04", title: "Mess Gone", desc: "Space cleared, floor swept if needed. We leave it better than we found it." },
];

const WHY = [
  { icon: Zap, title: "Fast Scheduling", desc: "Often same-week — sometimes same-day for urgent jobs." },
  { icon: DollarSign, title: "Fair, Upfront Pricing", desc: "You get the price before we lift a box. No surprises." },
  { icon: HardHat, title: "Construction Experience", desc: "We work alongside NJI Builders. We know jobsites." },
  { icon: Check, title: "Clean & Respectful", desc: "We sweep up, protect floors, respect your property." },
  { icon: MapPin, title: "Locally Operated", desc: "Local crew, local trucks, local reputation on the line." },
  { icon: Clock, title: "Reliable Turn-up", desc: "We show up on time. If we're running late, you'll know." },
];

export default function Home() {
  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b-[3px] border-black bg-white"
      >
        <div className="absolute inset-0 grid-lines opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-24 grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border-[2px] border-black bg-[#FFFFFF] px-3 py-1 mb-6">
              <div className="h-2 w-2 rounded-full bg-black animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-widest">Booking This Week</span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-display uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-black"
            >
              We clear the mess<br/>
              <span className="relative inline-block">
                <span className="relative z-10">so you don&apos;t</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-[#3B82F6] -z-0" aria-hidden="true" />
              </span>{" "}
              have to.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-neutral-700 leading-relaxed">
              <span className="font-bold">NJI Cleanouts</span> — cleanouts, garbage removal & construction debris
              hauling for homeowners, contractors, landlords, and property owners across most of New Jersey.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="#quote"
                data-testid="hero-cta-quote"
                className="inline-flex items-center justify-center gap-2 border-[3px] border-black bg-[#3B82F6] px-6 py-4 text-base font-bold uppercase tracking-wider text-white shadow-brutal btn-brutal-hover"
              >
                Get Free Quote <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="tel:+19733069206"
                data-testid="hero-cta-call"
                className="inline-flex items-center justify-center gap-2 border-[3px] border-black bg-white px-6 py-4 text-base font-bold uppercase tracking-wider shadow-brutal btn-brutal-hover"
              >
                <Phone className="h-5 w-5" /> (973) 306-9206
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              {["Same-week booking", "Fair upfront pricing", "Fully insured crew", "Local family-run"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-neutral-800">
                  <Check className="h-4 w-4 text-[#3B82F6]" strokeWidth={3} />
                  <span className="font-semibold">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -left-4 h-full w-full hazard-stripe" aria-hidden="true" />
              <div className="relative border-[3px] border-black bg-white p-8 md:p-10 shadow-brutal-lg">
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
                  Free estimate
                </div>
                <div className="font-display uppercase text-4xl md:text-5xl leading-[0.95] mb-6">
                  Call us for a<br/>
                  <span className="text-[#3B82F6]">fast, honest</span><br/>
                  quote.
                </div>
                <p className="text-neutral-700 mb-6 leading-relaxed">
                  Every job is different. A quick phone call gets you a fair price — no forms, no waiting, no games.
                </p>
                <a
                  href="tel:+19733069206"
                  data-testid="hero-side-call"
                  className="flex items-center justify-between gap-3 border-[3px] border-black bg-[#3B82F6] text-white px-5 py-4 shadow-brutal btn-brutal-hover"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-90">Tap to call</div>
                    <div className="font-display uppercase text-2xl md:text-3xl leading-none mt-1">
                      (973) 306-9206
                    </div>
                  </div>
                  <Phone className="h-8 w-8 shrink-0" />
                </a>
                <div className="mt-5 flex items-center gap-3 border-t-[3px] border-black pt-5">
                  <div className="flex h-10 w-10 items-center justify-center border-[3px] border-black bg-[#FFFFFF] shrink-0">
                    <Clock className="h-5 w-5 text-black" />
                  </div>
                  <div className="text-sm text-neutral-700">
                    Answering calls <span className="font-bold">Mon–Sat</span>. Usually same-day scheduling.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-b-[3px] border-black bg-black text-white overflow-hidden" aria-hidden="true">
        <div className="marquee py-3">
          <div className="marquee-track font-display uppercase tracking-widest text-xl md:text-2xl">
            {Array.from({ length: 2 }).map((_, r) => (
              <div key={r} className="flex gap-8 shrink-0 pr-8">
                {["Residential Cleanouts", "★", "Construction Debris", "★", "Dump Runs", "★", "Full Truckloads", "★",
                  "Fair Pricing", "★", "Local Crew", "★", "Fast Scheduling", "★", "Jobsite Cleanup", "★"].map((w, i) => (
                  <span key={i} className={w === "★" ? "text-[#3B82F6]" : ""}>{w}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="services-section" className="border-b-[3px] border-black bg-[#F7F5F1] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">— What we haul —</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95]">
              Three services. One <span className="text-[#3B82F6]">no-nonsense</span> crew.
            </h2>
            <p className="mt-4 text-neutral-700 text-lg max-w-2xl">
              From a couch that needs to disappear to a full jobsite that needs sweeping, we handle it all — fast, clean, and fairly priced.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group border-[3px] border-black bg-white p-8 shadow-brutal-sm hover:shadow-brutal-lg hover:-translate-y-1 hover:-translate-x-1 transition-all"
              >
                <div className="flex h-14 w-14 items-center justify-center border-[3px] border-black bg-[#FFFFFF] mb-5">
                  <s.icon className="h-7 w-7 text-black" strokeWidth={2.25} />
                </div>
                <h3 className="font-display uppercase text-2xl leading-tight mb-3">{s.title}</h3>
                <p className="text-neutral-700 text-base leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Pricing Note */}
          <div data-testid="pricing-note" className="mt-10 border-[3px] border-black bg-black text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
            <div className="border-[3px] border-[#FFFFFF] bg-black px-4 py-2">
              <span className="font-display uppercase text-[#FFFFFF] text-lg tracking-wider">Pricing note</span>
            </div>
            <p className="text-neutral-200 leading-relaxed">
              Construction debris (drywall, wood, tile, flooring, cabinets, doors, trim) is priced at our
              regular dump-truck load price <span className="font-bold text-[#FFFFFF]">+ $50 or more</span> due to heavier materials, labor, and dump fees.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" data-testid="how-section" className="border-b-[3px] border-black bg-white py-20 md:py-28 relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 md:px-8 relative">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">— How it works —</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95]">
              Four steps. Zero headaches.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} className="relative border-[3px] border-black bg-[#F7F5F1] p-6 shadow-brutal-sm">
                <div className="font-display text-6xl leading-none text-[#3B82F6] mb-4">{s.n}</div>
                <h3 className="font-display uppercase text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-700 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" data-testid="why-section" className="border-b-[3px] border-black bg-black text-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-12 lg:grid-cols-12 items-end">
            <div className="lg:col-span-6">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FFFFFF] mb-3">— Why choose us —</div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95]">
                We show up. <br /><span className="text-[#3B82F6]">We clean up.</span> <br />We move on.
              </h2>
              <p className="mt-6 text-neutral-300 text-lg max-w-lg">
                NJI Cleanouts is the sister crew to NJI Builders. Same standards. Same work ethic. Same respect for your property.
              </p>
            </div>
            <div className="lg:col-span-6 grid gap-4 sm:grid-cols-2">
              {WHY.map((w) => (
                <div key={w.title} className="border-[3px] border-white bg-black p-5">
                  <div className="flex h-10 w-10 items-center justify-center border-[2px] border-[#FFFFFF] bg-black mb-3">
                    <w.icon className="h-5 w-5 text-[#FFFFFF]" strokeWidth={2.25} />
                  </div>
                  <h3 className="font-display uppercase text-lg mb-1">{w.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" data-testid="quote-section" className="border-b-[3px] border-black bg-[#F7F5F1] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">— Request a callback —</div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95]">
                Can&apos;t talk now? <br /><span className="text-[#3B82F6]">We&apos;ll call you.</span>
              </h2>
              <p className="mt-6 text-neutral-700 text-lg max-w-md">
                Drop your info and a short description of the job. We&apos;ll call you back to go over details and give you a fair, honest quote by phone.
              </p>
              <div className="mt-6 border-[3px] border-black bg-white p-5 shadow-brutal-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center border-[3px] border-black bg-[#FFFFFF]">
                    <Phone className="h-5 w-5 text-black" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Prefer to call now?</div>
                    <a href="tel:+19733069206" className="font-display uppercase text-2xl leading-none block mt-1">
                      (973) 306-9206
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" data-testid="contact-section" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-10 md:grid-cols-2 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">— Contact —</div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] mb-6">
                Ready to <span className="text-[#3B82F6]">clear it out?</span>
              </h2>
              <p className="text-neutral-700 text-lg max-w-lg">
                Call, text, or email. We respond fast — usually same day.
              </p>
            </div>
            <div className="space-y-4">
              <a
                href="tel:+19733069206"
                data-testid="contact-phone-card"
                className="block border-[3px] border-black bg-[#0A0A0A] p-6 md:p-8 shadow-brutal btn-brutal-hover text-white"
              >
                <div className="text-xs uppercase tracking-widest opacity-80">Call or text</div>
                <div className="font-display uppercase text-4xl md:text-5xl mt-1 flex items-center gap-3">
                  <Phone className="h-8 w-8" />
                  (973) 306-9206
                </div>
              </a>
              <a
                href="mailto:njicleanout@gmail.com"
                data-testid="contact-email-card"
                className="block border-[3px] border-black bg-black p-6 md:p-8 shadow-brutal btn-brutal-hover text-white"
              >
                <div className="text-xs uppercase tracking-widest text-[#FFFFFF]">Email</div>
                <div className="font-display uppercase text-xl md:text-2xl mt-1 flex items-center gap-3 break-all">
                  <Mail className="h-6 w-6 shrink-0" />
                  njicleanout@gmail.com
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
